import { Injectable, inject } from '@angular/core';
import {BehaviorSubject, Observable, take, throwError} from 'rxjs';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {BlockUiService} from '@shared/services';
import {SecurityContext} from './security-context';
import {ApplicationRepository} from '../mt-repository/application-repository';
import {RepositoryKey} from '../mt-repository/repository-key';
import {RepositoryType} from '../mt-repository/repository';
import {AuthenticationService} from "./authentication.service";
import {AuthUserInfo} from "./auth-user-info";

@Injectable({
    providedIn: 'root'
})
export class ErrorInterceptor implements HttpInterceptor {
    private router = inject(Router);
    private applicationRepository = inject(ApplicationRepository);
    private blockUiService = inject(BlockUiService);
    private authenticationService = inject(AuthenticationService);
    private securityContext = inject(SecurityContext);

    private isRefreshTokenInProgress: boolean = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                    if (error.url.includes('auth/refresh')) {
                        this.applicationRepository.setItem(RepositoryKey.LAST_SEEN_PAGE, this.router.url, RepositoryType.LOCAL)
                        this.securityContext.clear();
                        this.isRefreshTokenInProgress = false;
                        this.router.navigateByUrl('/login');
                        return throwError(() => error);
                    }
                    if (this.router.url !== '/login') {
                        if (this.isRefreshTokenInProgress) {
                            return this.refreshTokenSubject.pipe(
                                filter(token => token != null),
                                take(1),
                                switchMap(jwt => {
                                    return next.handle(this.getAuthRequest(req, jwt));
                                })
                            );
                        } else {
                            this.isRefreshTokenInProgress = true;
                            this.refreshTokenSubject.next(null);

                            return this.authenticationService.refreshToken().pipe(
                                switchMap((data: AuthUserInfo) => {
                                    this.securityContext.setAccessToken(data.access_token);
                                    this.isRefreshTokenInProgress = false;
                                    this.refreshTokenSubject.next(data.access_token);
                                    return next.handle(this.getAuthRequest(req, data.access_token));
                                })
                            );
                        }
                    }
                }
                if (error.status === 403) {
                    this.router.navigateByUrl('/access-denied');
                    this.blockUiService.stopBlock();
                }
                return throwError(() => error);
            }));
    }

    private getAuthRequest(req: HttpRequest<any>, token: string) {
        return req.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`,
            }
        });
    }
}
